/**
 * Walk panel — author the walking path (floor plane + walkable boundaries on
 * one or more levels, plus stair connectors).
 *
 * Sections (top → bottom):
 *   - Edit button (toggles WalkTool active state)
 *   - Live status / guidance line (what to click next)
 *   - Floor readout
 *   - Levels list (ground + extra floors; click a row to edit its boundary)
 *   - Stairs list (quad ramps between two heights)
 *   - Show-in-viewport toggle, eye-height input
 *   - Reset floor / Clear buttons
 *   - Walk preview (pick the viewer's starting position)
 *
 * Mirrors markers-panel.ts structure + class conventions so the same CSS
 * applies. All state lives in the WalkArea element; this panel just drives it
 * via the `walk.*` event namespace and reflects its state.
 */

import {
    Container, Label, Button, BooleanInput, NumericInput
} from '@playcanvas/pcui';

import type { Events } from '../../events';
import type { Scene } from '../../scene';
import { i18n } from '../../ui/localization';
import { levelRGB, STAIR_RGB, type WalkArea } from '../walk-area';

type WalkToolMode = 'floor' | 'boundary' | 'level-height' | 'stairs';

class WalkPanel extends Container {
    constructor(events: Events, scene: Scene, args = {}) {
        args = {
            ...args,
            id: 'walk-panel',
            class: 'panel',
            hidden: true
        };
        super(args);

        // Block viewport gestures from passing through the panel.
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((evt) => {
            this.dom.addEventListener(evt, (e: Event) => e.stopPropagation());
        });

        const walkEl = (): WalkArea | undefined => (scene as any).walkArea;

        let toolActive = false;
        let toolMode: WalkToolMode = 'floor';
        let transientStatus = '';

        const fmt = (n: number) => n.toFixed(2);

        /** Small colored ● matching the viewport boundary color. */
        const colorDot = (rgb: readonly number[]): Label => {
            const dot = new Label({ class: 'walk-list-dot', text: '●' });
            dot.dom.style.color = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
            return dot;
        };

        const ensureTool = () => { if (!toolActive) events.fire('tool.walk'); };

        // ── Header ──────────────────────────────────────────────────────
        const header = new Container({ class: 'panel-header' });
        header.append(new Label({ class: 'panel-header-icon', text: '' }));
        header.append(new Label({ class: 'panel-header-label', text: i18n.t('ollo.walk.title') }));
        this.append(header);

        // ── Primary action: Edit ────────────────────────────────────────
        const primaryButtons = new Container({ class: 'walk-buttons' });
        const editButton = new Button({ class: ['walk-button-primary'], text: i18n.t('ollo.walk.edit') });
        editButton.on('click', () => events.fire('tool.walk'));
        primaryButtons.append(editButton);
        this.append(primaryButtons);

        // ── Live guidance line ──────────────────────────────────────────
        const statusLabel = new Label({ class: 'walk-hint', text: '' });
        this.append(statusLabel);

        // ── Readouts ────────────────────────────────────────────────────
        const floorRow = new Container({ class: 'walk-row' });
        floorRow.append(new Label({ class: 'walk-row-label', text: i18n.t('ollo.walk.floor') }));
        const floorValue = new Label({ class: 'walk-row-value', text: i18n.t('ollo.walk.not-set') });
        floorRow.append(floorValue);
        this.append(floorRow);

        // ── Levels ──────────────────────────────────────────────────────
        const levelsHeader = new Container({ class: 'walk-row' });
        levelsHeader.append(new Label({ class: 'walk-row-label', text: i18n.t('ollo.walk.levels') }));
        const addLevelButton = new Button({ class: 'walk-small-button', text: i18n.t('ollo.walk.add-level') });
        addLevelButton.on('click', () => {
            const wa = walkEl();
            if (!wa?.hasFloor) {
                ensureTool();
                events.fire('walk.status', 'Set the ground floor first — click 3 points on the floor.');
                return;
            }
            ensureTool();
            events.fire('walk.level.add');
        });
        levelsHeader.append(addLevelButton);
        this.append(levelsHeader);

        const levelsList = new Container({ class: 'walk-list' });
        this.append(levelsList);

        const rebuildLevels = () => {
            levelsList.clear();
            const wa = walkEl();
            if (!wa) return;

            const makeRow = (idx: number, name: string, pts: number) => {
                const row = new Container({ class: 'walk-list-row' });
                if (wa.activeLevel === idx) row.class.add('walk-list-row-active');

                const dot = colorDot(levelRGB(idx));
                row.append(dot);
                const nameLabel = new Label({ class: 'walk-list-name', text: name });
                row.append(nameLabel);

                // Click the name/dot to make this the level being edited.
                const select = () => {
                    events.fire('walk.level.select', idx);
                    ensureTool();
                };
                dot.dom.addEventListener('click', select);
                nameLabel.dom.addEventListener('click', select);

                return { row, ptsLabel: new Label({ class: 'walk-list-value', text: `${pts} pts` }) };
            };

            // Ground row — height is fixed by the leveled floor plane.
            {
                const { row, ptsLabel } = makeRow(0, 'Ground', wa.boundary.length);
                const h = wa.hasFloor ? (wa.plane!.origin[1]) : null;
                row.append(new Label({
                    class: 'walk-list-value',
                    text: h === null ? '—' : `Y ${fmt(h)}`
                }));
                row.append(ptsLabel);
                levelsList.append(row);
            }

            // Extra levels — editable height, removable.
            wa.levels.forEach((level, i) => {
                const idx = i + 1;
                const { row, ptsLabel } = makeRow(idx, `Level ${idx + 1}`, level.boundary.length);

                const heightInput = new NumericInput({
                    class: 'walk-list-input',
                    value: level.height ?? 0,
                    precision: 2,
                    step: 0.1,
                    min: -1000,
                    max: 1000
                });
                heightInput.on('change', (v: number) => {
                    if (level.height !== null && Math.abs(v - level.height) < 1e-9) return;
                    events.fire('walk.level.height', idx, v);
                });
                row.append(heightInput);
                row.append(ptsLabel);

                const removeButton = new Button({ class: 'walk-small-button', text: '✕' });
                removeButton.on('click', async () => {
                    if (level.boundary.length >= 3) {
                        const r = await events.invoke('showPopup', {
                            type: 'yesno',
                            header: 'Remove level?',
                            message: `Delete Level ${idx + 1} and its ${level.boundary.length}-point boundary?`
                        });
                        if (r?.action !== 'yes') return;
                    }
                    events.fire('walk.level.remove', idx);
                });
                row.append(removeButton);

                levelsList.append(row);
            });
        };

        // ── Stairs ──────────────────────────────────────────────────────
        const stairsHeader = new Container({ class: 'walk-row' });
        stairsHeader.append(new Label({ class: 'walk-row-label', text: i18n.t('ollo.walk.stairs') }));
        const addStairsButton = new Button({ class: 'walk-small-button', text: i18n.t('ollo.walk.add-stairs') });
        addStairsButton.on('click', () => {
            const wa = walkEl();
            if (!wa?.hasFloor) {
                ensureTool();
                events.fire('walk.status', 'Set the ground floor first — click 3 points on the floor.');
                return;
            }
            // A flight runs between two floors, so it needs at least two
            // complete levels (height + ≥3 boundary points) to click on.
            if (wa.pickableLevelSurfaces().length < 2) {
                ensureTool();
                events.fire('walk.status', 'Stairs connect two floors — add a level (set its height and draw its boundary) first.');
                return;
            }
            ensureTool();
            events.fire('walk.stairs.begin');
        });
        stairsHeader.append(addStairsButton);
        this.append(stairsHeader);

        const stairsList = new Container({ class: 'walk-list' });
        this.append(stairsList);

        const rebuildStairs = () => {
            stairsList.clear();
            const wa = walkEl();
            if (!wa) return;
            wa.stairs.forEach((s, i) => {
                const row = new Container({ class: 'walk-list-row' });
                row.append(colorDot(STAIR_RGB));
                row.append(new Label({ class: 'walk-list-name', text: `Flight ${i + 1}` }));

                const lowInput = new NumericInput({
                    class: 'walk-list-input',
                    value: s.lowHeight,
                    precision: 2,
                    step: 0.1,
                    min: -1000,
                    max: 1000
                });
                lowInput.on('change', (v: number) => {
                    if (Math.abs(v - s.lowHeight) < 1e-9) return;
                    events.fire('walk.stairs.heights', i, v, s.highHeight);
                });
                row.append(lowInput);

                row.append(new Label({ class: 'walk-list-value', text: '→' }));

                const highInput = new NumericInput({
                    class: 'walk-list-input',
                    value: s.highHeight,
                    precision: 2,
                    step: 0.1,
                    min: -1000,
                    max: 1000
                });
                highInput.on('change', (v: number) => {
                    if (Math.abs(v - s.highHeight) < 1e-9) return;
                    events.fire('walk.stairs.heights', i, s.lowHeight, v);
                });
                row.append(highInput);

                const removeButton = new Button({ class: 'walk-small-button', text: '✕' });
                removeButton.on('click', () => events.fire('walk.stairs.remove', i));
                row.append(removeButton);

                stairsList.append(row);
            });
        };

        // ── Options ─────────────────────────────────────────────────────
        const visRow = new Container({ class: 'walk-row' });
        visRow.append(new Label({ class: 'walk-row-label', text: i18n.t('ollo.walk.show') }));
        const visToggle = new BooleanInput({ value: true });
        visToggle.on('change', (v: boolean) => events.fire('walk.setVisible', v));
        visRow.append(visToggle);
        this.append(visRow);

        // ── Collision voxels (floor + walls from a .voxel.json/.bin pair) ──
        const voxRow = new Container({ class: 'walk-row' });
        voxRow.append(new Label({ class: 'walk-row-label', text: i18n.t('ollo.walk.voxels') }));
        const voxValue = new Label({ class: 'walk-row-value', text: i18n.t('ollo.walk.voxels-none') });
        voxRow.append(voxValue);
        this.append(voxRow);

        const voxShowRow = new Container({ class: 'walk-row' });
        voxShowRow.append(new Label({ class: 'walk-row-label', text: i18n.t('ollo.walk.voxels-show') }));
        const voxToggle = new BooleanInput({ value: true });
        voxToggle.on('change', (v: boolean) => events.fire('walk.voxels.setVisible', v));
        voxShowRow.append(voxToggle);
        this.append(voxShowRow);

        const voxButtons = new Container({ class: 'walk-buttons' });
        const voxLoadButton = new Button({ text: i18n.t('ollo.walk.voxels-load') });
        voxLoadButton.on('click', () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.accept = '.json,.bin';
            input.onchange = () => {
                if (input.files?.length) events.fire('walk.voxels.loadFiles', Array.from(input.files));
            };
            input.click();
        });
        voxButtons.append(voxLoadButton);
        const voxClearButton = new Button({ text: i18n.t('ollo.walk.voxels-clear') });
        voxClearButton.on('click', () => events.fire('walk.voxels.clear'));
        voxButtons.append(voxClearButton);
        this.append(voxButtons);

        const syncVoxels = () => {
            const vox = events.invoke('walk.voxels') as { grid: unknown; url: string; drawn: number } | undefined;
            const name = vox?.grid ? vox.url.split('/').pop() : '';
            voxValue.text = name ? `${name} · ${vox.drawn.toLocaleString()} shown` : i18n.t('ollo.walk.voxels-none');
            voxClearButton.enabled = !!name;
        };
        events.on('walk.voxels.changed', syncVoxels);
        events.on('walk.voxels.drawn', syncVoxels);
        events.on('walk.voxels.visibleChanged', (v: boolean) => { if (voxToggle.value !== v) voxToggle.value = v; });

        const eyeRow = new Container({ class: 'walk-row' });
        eyeRow.append(new Label({ class: 'walk-row-label', text: i18n.t('ollo.walk.eye-height') }));
        const eyeInput = new NumericInput({ value: 1.75, precision: 2, step: 0.05, min: 0.1, max: 10 });
        eyeInput.on('change', (v: number) => events.fire('walk.eyeHeight', v));
        eyeRow.append(eyeInput);
        this.append(eyeRow);

        // ── Buttons ─────────────────────────────────────────────────────
        const buttons = new Container({ class: 'walk-buttons' });
        const resetFloorButton = new Button({ text: i18n.t('ollo.walk.reset-floor') });
        resetFloorButton.on('click', () => {
            events.fire('walk.floor.reset');
            if (!toolActive) events.fire('tool.walk');
        });
        buttons.append(resetFloorButton);

        const clearBoundaryButton = new Button({ text: i18n.t('ollo.walk.clear-boundary') });
        clearBoundaryButton.on('click', () => {
            events.fire('walk.boundary.clear');
            if (!toolActive) events.fire('tool.walk');
        });
        buttons.append(clearBoundaryButton);

        const clearButton = new Button({ text: i18n.t('ollo.walk.clear-all') });
        clearButton.on('click', async () => {
            const wa = walkEl();
            if (!wa || (!wa.hasFloor && wa.boundary.length === 0 &&
                wa.levels.length === 0 && wa.stairs.length === 0)) return;
            const r = await events.invoke('showPopup', {
                type: 'yesno',
                header: 'Clear walk area?',
                message: 'Delete the floor plane, every level boundary and all stairs?'
            });
            if (r?.action === 'yes') events.fire('walk.clear');
        });
        buttons.append(clearButton);
        this.append(buttons);

        // ── Walk preview (pick the viewer's starting position) ───────────
        const previewButtons = new Container({ class: 'walk-buttons' });
        const walkHereButton = new Button({ class: ['walk-button-primary'], text: i18n.t('ollo.walk.walk-here') });
        walkHereButton.on('click', () => events.fire('walk.preview.toggle'));
        previewButtons.append(walkHereButton);
        this.append(previewButtons);

        const setStartButton = new Button({ text: i18n.t('ollo.walk.start-view') });
        setStartButton.enabled = false;
        setStartButton.on('click', () => {
            events.fire('camera.setStartPose');
            events.fire('walk.status', 'Starting view set to where you’re standing.');
        });
        this.append(setStartButton);

        let previewActive = false;
        events.on('walk.preview.changed', (active: boolean) => {
            previewActive = active;
            walkHereButton.text = active ? '◉ Walking (Esc to exit)' : 'Walk here';
            if (active) walkHereButton.class.add('walk-button-active');
            else walkHereButton.class.remove('walk-button-active');
            setStartButton.enabled = active;
            syncAll();
        });

        const helpLabel = new Label({
            class: 'walk-hint',
            text: i18n.t('ollo.walk.hint')
        });
        this.append(helpLabel);

        // ── Live sync ───────────────────────────────────────────────────
        const levelName = (i: number) => (i === 0 ? 'Ground' : `Level ${i + 1}`);

        const computeGuidance = (): string => {
            const wa = walkEl();
            if (!wa) return '';
            if (transientStatus) return transientStatus;
            if (!wa.hasFloor) {
                const n = wa.picks.length;
                return toolActive
                    ? `Set the floor — click 3 points on the floor (${n}/3 placed).`
                    : 'Click "Edit walk area", then click 3 points on the floor.';
            }
            if (toolActive && toolMode === 'level-height') {
                return `Click a point ON that floor to set ${levelName(wa.activeLevel)}’s height (or type it in the list).`;
            }
            if (toolActive && toolMode === 'stairs') {
                switch (wa.stairsDraft.length) {
                    case 0: return 'Stairs (1/4): click a floor where one SIDE of the flight starts — the point snaps to the area’s edge.';
                    case 1: return 'Stairs (2/4): click the OTHER floor where that side ends (snaps to its edge) — the sloped line is the first limit of the flight.';
                    case 2: return 'Stairs (3/4): first side set. Click either floor where the OTHER side starts.';
                    default: return 'Stairs (4/4): click the opposite floor to finish. Backspace undoes a pick.';
                }
            }
            const b = wa.activeBoundary().length;
            const name = levelName(wa.activeLevel);
            if (b < 3) {
                return toolActive
                    ? `${name}: click around that floor to add boundary points (${b} placed).`
                    : `${name}: click "Edit walk area" to draw the boundary.`;
            }
            return `${name} boundary ready — ${b} points. Drag a point to adjust; Backspace removes the last.`;
        };

        const syncAll = () => {
            const wa = walkEl();
            floorValue.text = wa?.hasFloor ? 'set' : (wa ? `not set (${wa.picks.length}/3)` : 'not set');
            if (wa && eyeInput.value !== wa.eyeHeight) eyeInput.value = wa.eyeHeight;
            rebuildLevels();
            rebuildStairs();
            // "Walk here" needs a finished ground area (floor + ≥3 boundary points).
            const ready = !!(wa && wa.hasFloor && wa.boundary.length >= 3);
            walkHereButton.enabled = ready || previewActive;
            statusLabel.text = computeGuidance();
        };

        events.on('walk.changed', () => { transientStatus = ''; syncAll(); });
        events.on('walk.status', (msg: string) => { transientStatus = msg; statusLabel.text = msg; });
        events.on('walk.visibleChanged', (v: boolean) => { if (visToggle.value !== v) visToggle.value = v; });
        events.on('walk.tool.mode', (m: WalkToolMode) => { toolMode = m; transientStatus = ''; syncAll(); });

        events.on('tool.activated', (toolName: string) => {
            if (toolName === 'walk') {
                toolActive = true;
                editButton.text = i18n.t('ollo.walk.editing');
                editButton.class.add('walk-button-active');
                transientStatus = '';
                syncAll();
            }
        });
        events.on('tool.deactivated', (toolName: string) => {
            if (toolName === 'walk') {
                toolActive = false;
                editButton.text = i18n.t('ollo.walk.edit');
                editButton.class.remove('walk-button-active');
                syncAll();
            }
        });

        // Initial sync (WalkArea may not exist on the construction frame).
        setTimeout(syncAll, 0);
    }
}

export { WalkPanel };
